import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Tabs, Tab } from 'react-bootstrap/lib';
import { bindActionCreators } from 'redux';
import SidebarContainer from '../SidebarContainer';
import * as analyticsActions from '../../redux/analytics/actions';
import {
  ResultsStore,
  ResultsOnline,
  ResultsOverview,
} from '../../components/ResultsDashboard';
import { getHash } from '../../router';
import './styles.scss';
import { useQueryDate } from './hooks/useQueryDate';

const TAB_KEYS = {
  ResultsOverview: 'overview',
  ResultsStore: 'in-store',
  ResultsOnline: 'online',
};

function ResultsDashboard(props) {
  const isOnlineOnly =
    props.dealer.crmConfig && props.dealer.crmConfig.adfXMLOnly;
  const { actions, location, dealer } = props;

  const getActiveTabFromHash = () => {
    const hash = getHash();
    if (hash) {
      for (const [key, value] of Object.entries(TAB_KEYS)) {
        if (value === hash) return TAB_KEYS[key];
      }
    }
    return TAB_KEYS.ResultsOverview;
  };

  // defining date range in parent component
  const [queryDate, setQueryDate] = useQueryDate(props);
  const [activeTabKey, setActiveTabKey] = useState(getActiveTabFromHash());

  // Re-fetch analytics whenever a dealer changes
  // Anytime a child component changes location, we fetch not all but corresponding data
  // based on which tab they are on (to avoid unecessary requests)
  useEffect(() => {
    const { start, end } = queryDate;
    const analyticsParams = {
      dealerId: dealer.id,
      startDate: start.toISOString(),
      endDate: end.toISOString(),
    };
    if (!isOnlineOnly) {
      if (activeTabKey === TAB_KEYS.ResultsOverview)
        actions.analyticsOverviewRequest(analyticsParams);
      else if (activeTabKey === TAB_KEYS.ResultsStore)
        actions.analyticsStoreRequest(analyticsParams);
    }
    if (activeTabKey === TAB_KEYS.ResultsOnline)
      actions.analyticsOnlineRequest(analyticsParams);
    // eslint-disable-next-line
  }, [dealer, actions, location, activeTabKey, isOnlineOnly]);

  const onDateRangeChange = (start, end) => {
    // We want to persist the date in query string, so if the user goes to another component
    // it can be parsed and set in the state
    // this is being set via the custom hook
    setQueryDate({
      start,
      end,
    });
  };

  return (
    <SidebarContainer title={props.dealer.name}>
      <div id="results" className="tabbed-page-container">
        <Tabs
          id="results-tabs"
          mountOnEnter
          unmountOnExit
          defaultActiveKey={activeTabKey}
          onSelect={(activeKey) => setActiveTabKey(activeKey)}
        >
          {!isOnlineOnly && (
            <Tab eventKey={TAB_KEYS.ResultsOverview} title="Overview">
              <ResultsOverview
                queryDate={{ ...queryDate }}
                onDateRangeChange={onDateRangeChange}
                analytics={props.overview}
                fetchAnalytics={props.actions.analyticsOverviewRequest}
              />
            </Tab>
          )}
          {!isOnlineOnly && (
            <Tab eventKey={TAB_KEYS.ResultsStore} title="Instore Results">
              <ResultsStore
                queryDate={{ ...queryDate }}
                dealer={dealer}
                onDateRangeChange={onDateRangeChange}
                analytics={props.storeAnalytics}
                fetchAnalytics={props.actions.analyticsStoreRequest}
              />
            </Tab>
          )}
          <Tab eventKey={TAB_KEYS.ResultsOnline} title="Online Results">
            <ResultsOnline
              fetchLocal
              queryDate={{ ...queryDate }}
              deals={props.onlineDeals}
              onDateRangeChange={onDateRangeChange}
              analytics={props.onlineAnalytics}
              fetchAnalytics={props.actions.analyticsOnlineRequest}
              fetchDeals={props.actions.analyticsOnlineDealsRequest}
            />
          </Tab>
        </Tabs>
      </div>
    </SidebarContainer>
  );
}

ResultsDashboard.propTypes = {
  location: PropTypes.object.isRequired,
  actions: PropTypes.object.isRequired,
  dealer: PropTypes.object.isRequired,
  overview: PropTypes.object.isRequired,
  onlineDeals: PropTypes.object.isRequired,
  onlineAnalytics: PropTypes.object.isRequired,
  storeAnalytics: PropTypes.object.isRequired,
};

const mapStateToProps = ({ analytics, user }) => ({
  dealer: user.currentUserDealer,
  overview: analytics.overview,
  onlineDeals: analytics.onlineDeals,
  onlineAnalytics: analytics.onlineAnalytics,
  storeAnalytics: analytics.storeAnalytics,
});

const mapDispatchToProps = (dispatch) => ({
  actions: bindActionCreators(analyticsActions, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(ResultsDashboard);
